// client/src/utils/CivilChatEngine.js
import { CivilEngineeringData } from "./CivilEngineeringData";

export const CivilChatEngine = {
  processMessage: (input) => {
    const text = input.toLowerCase().trim();

    // --- 1. INTENT: VASTU ADVISORY ---
    if (text.includes("vastu")) {
      const directions = ["north", "south", "east", "west", "kitchen", "bedroom", "pooja"];
      const direction = directions.find((d) => text.includes(d));

      if (direction) {
        let data;
        if (["north", "south", "east", "west"].includes(direction)) {
          data = CivilEngineeringData.vastu[direction];
          return `🏠 **Vastu for ${direction.charAt(0).toUpperCase() + direction.slice(1)} Facing/Direction**:\n\n` +
                 `*   ${data.tips.join("\n*   ")}\n\n` +
                 `💡 **Reasoning**: ${data.reasoning}`;
        } else {
          // Specific rooms
          data = CivilEngineeringData.vastu[direction];
          let response = `🏠 **Vastu for ${direction.charAt(0).toUpperCase() + direction.slice(1)}**:\n\n`;
          if (data.best) response += `*   ✅ **Best Position**: ${data.best}\n`;
          if (data.alternative) response += `*   👌 **Alternative**: ${data.alternative}\n`;
          if (data.avoid) response += `*   ❌ **Avoid**: ${data.avoid}\n`;
          if (data.tip) response += `*   💡 **Tip**: ${data.tip}\n`;
          
          if (direction === "bedroom") {
             response = `🏠 **Vastu for Bedroom**:\n\n` +
                        `*   👑 **Master Bedroom**: ${data.master}\n` +
                        `*   🧸 **Kids Room**: ${data.kids}\n` +
                        `*   🛏️ **Guest Room**: ${data.guest}`;
          }
          return response;
        }
      }
    }

    // --- 2. INTENT: MATERIAL CALCULATION (e.g., "steel for 1m cube footing") ---
    if (text.includes("calculate") || text.includes("amount") || text.includes("quantity") || text.includes("how much") || text.includes("steel") || text.includes("bricks")) {
      
      // Extract Number (Volume/Area)
      const numberMatch = text.match(/(\\d+(\\.\\d+)?)/);
      const val = numberMatch ? parseFloat(numberMatch[0]) : 1; // Default to 1 if no number found but intent is clear
      
      // Steel Logic
      if (text.includes("steel")) {
        let member = "footing"; // default
        if (text.includes("column")) member = "column";
        if (text.includes("beam")) member = "beam";
        if (text.includes("slab")) member = "slab";

        const formula = CivilEngineeringData.formulas[`steel_${member}`];
        const steelKg = formula.calc(val);
        
        return `🧮 **Steel Estimation**:\n\n` +
               `For **${val} m³** of **${member}** concrete:\n` +
               `*   **Approx Steel**: ${steelKg.toFixed(2)} Kg\n` +
               `*   **Basis**: ${formula.rule}\n` +
               `*   **Density**: 7850 kg/m³\n\n` +
               `_(Note: This is a thumb rule. Exact quantity depends on structural design.)_`;
      }

      // Bricks Logic
      if (text.includes("brick")) {
        const bricks = CivilEngineeringData.formulas.bricks.standard.calc(val);
        return `🧱 **Brick Estimation**:\n\n` +
               `For **${val} m³** of brickwork:\n` +
               `*   **Approx Bricks**: ~${bricks} nos.\n` +
               `*   **Size**: Standard Modular Brick (with mortar)`;
      }
    }

    // --- 3. INTENT: COMPARISON ("Compare OPC and PPC") ---
    if (text.includes("compare") || text.includes("vs") || text.includes("difference")) {
      const m = CivilEngineeringData.materials.cement;
      if ((text.includes("opc") && text.includes("ppc")) || (text.includes("43") && text.includes("53"))) {
         return `⚖️ **Comparison: OPC vs PPC**\n\n` +
                `| Feature | Ordinary Portland Cement (OPC) | Portland Pozjolana Cement (PPC) |\n` +
                `| :--- | :--- | :--- |\n` +
                `| **Strength** | High initial strength (good for fast de-shuttering) | Gain strength slowly but equals OPC eventually |\n` +
                `| **Curing** | Requires intense curing | Requires longer curing period |\n` +
                `| **Heat** | High heat (cracks risk) | Low heat (ideal for mass concrete) |\n` +
                `| **Durability** | Moderate | High (Chemical/Sulphate resistant) |\n` +
                `| **Best For** | Structures, Columns, Beams | Plastering, Masonry, Waterproofing |\n\n` +
                `💡 **Verdict**: Use **OPC** for structural members where speed is needed. Use **PPC** for finishing and home building to prevent cracks.`;
      }
    }

    // --- 4. INTENT: FACTUAL LOOKUP (Dictionary) ---
    // Check general dictionary
    for (const [key, answer] of Object.entries(CivilEngineeringData.general)) {
      if (text.includes(key)) {
        return `📖 **Info**: ${answer}`;
      }
    }

    // --- 5. INTENT: CONCRETE TECHNOLOGY (Slump & Mix) ---
    if (text.includes("slump") || (text.includes("mix") && text.includes("design"))) {
      
      // Slump Check
      if (text.includes("slump")) {
        const numberMatch = text.match(/(\d+)/);
        const slumpVal = numberMatch ? parseInt(numberMatch[0]) : null;

        if (slumpVal !== null) {
          // Find logic
          const slumpInfo = CivilEngineeringData.concrete.slump.find(s => {
             // Parse range "50-100 mm" -> 50, 100
             const parts = s.range.replace(/[^0-9-]/g, '').split('-');
             if (parts.length === 2) {
               return slumpVal >= parseInt(parts[0]) && slumpVal <= parseInt(parts[1]);
             } else if (s.range.includes(">")) {
               return slumpVal > 150;
             }
             return false;
          });

          if (slumpInfo) {
             return `💧 **Slump Analysis for ${slumpVal}mm**:\n\n` +
                    `*   **Category**: ${slumpInfo.type} Workability\n` +
                    `*   **Rec. Use**: ${slumpInfo.use}\n` +
                    `*   **Advise**: Suitable for pumping? ${slumpVal >= 80 ? "Yes ✅" : "No ❌ (Use bucket/chute)"}`;
          }
        }
        
        // General Slump Info
        return "💧 **Concrete Slump Guide**:\n" +
               CivilEngineeringData.concrete.slump.map(s => `*   **${s.range}**: ${s.use}`).join("\n");
      }

      // Mix Design General
      if (text.includes("mix")) {
         return `🧪 **Concrete Mix Design Steps (IS 10262)**:\n` +
                CivilEngineeringData.concrete.mix_design.steps.join("\n") + 
                `\n\n💡 **Tip**: For normal construction (M20/M25), use nominal mix. For M30+, design mix is mandatory.`;
      }
    }

    // --- 6. INTENT: NAVIGATION & SYSTEM FAQ ---
    if (text.includes("order") || text.includes("track")) {
      return "📦 **Order Tracking**:\nYou can track your orders in the **My Orders** section of your profile.\n\n[Go to My Orders](/customer/orders)";
    }
    if (text.includes("payment") || text.includes("pay")) {
      return "💳 **Payment Options**:\nWe accept:\n*   Credit/Debit Cards\n*   UPI\n*   Net Banking\n*   Wallet\n\nAll payments are secured via Razorpay.";
    }
    if (text.includes("return") || text.includes("refund")) {
      return "🔄 **Returns & Refunds**:\nReference our return policy in the footer. Generally, returns are accepted within 7 days for damaged goods.";
    }
    if (text.includes("contact") || text.includes("support") || text.includes("help") || text.includes("call")) {
      return "📞 **Customer Support**:\n*   Email: support@connectingconstructions.com\n*   Phone: +91 98765 43210\n*   Available: Mon-Sat, 9AM - 6PM";
    }

    // --- 6. FALLBACK / GENERAL CONVERSATION ---
    if (text.includes("hello") || text.includes("hi") || text.includes("hey")) {
      return "👋 **Hello!** I am your AI Civil Engineering Assistant.\n\nI can help you with:\n*   🏠 **Vastu** (e.g., 'Vastu for Kitchen')\n*   🧮 **Estimations** (e.g., 'Steel for 1m cube footing')\n*   ⚖️ **Comparisons** (e.g., 'OPC vs PPC')\n*   📦 **Orders** & Support";
    }

    return "🤔 I didn't quite catch that civil engineering query.\n\nTry asking me:\n*   'Vastu for North facing entrance'\n*   'Calculate steel for 5m cube slab'\n*   'Compare 43 and 53 grade cement'\n*   'Best cement for alkaline soil'";
  }
};
